import { useEffect, useRef, useState } from "react";
import { Maximize, Minimize, Pause, Play, Volume2, VolumeX, Loader2 } from "lucide-react";
import { NwSlider } from "@/components/player/NwSlider";
import { useVideoView } from "@/hooks/useVideoView";
import { formatTime } from "@/lib/format";

export const VideoPlayer = ({ video, onViewed }) => {
  const box = useRef(null), el = useRef(null);
  const { src, viewed, ...handlers } = useVideoView(video);
  useEffect(() => { if (viewed) onViewed?.(); }, [viewed]); // eslint-disable-line react-hooks/exhaustive-deps
  const [s, setS] = useState({ playing: false, loading: true, current: 0, duration: video.duration_seconds, muted: false, volume: 1, full: false });
  const set = (p) => setS((x) => ({ ...x, ...p }));

  useEffect(() => {
    const onFs = () => set({ full: !!document.fullscreenElement });
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggle = () => (el.current.paused ? el.current.play() : el.current.pause());
  const fullscreen = () => {
    if (document.fullscreenElement) return document.exitFullscreen();
    if (box.current.requestFullscreen) box.current.requestFullscreen();
    else el.current.webkitEnterFullscreen?.();
  };
  const setVolume = (v) => { el.current.volume = v; el.current.muted = v === 0; set({ volume: v, muted: v === 0 }); };
  const toggleMute = () => { el.current.muted = !s.muted; set({ muted: !s.muted }); };
  const vertical = video.height > video.width;

  return (
    <div ref={box} data-testid="video-player" className={`group relative overflow-hidden bg-black ${s.full ? "" : "rounded-2xl border border-white/10"} ${vertical && !s.full ? "mx-auto max-w-sm aspect-[9/16]" : "aspect-video"}`}>
      <video ref={el} src={src || undefined} poster={`${process.env.REACT_APP_BACKEND_URL}${video.thumbnail_url}`} playsInline preload="metadata" autoPlay
        data-testid="video-element" className="h-full w-full object-contain" onClick={toggle}
        onPlay={() => { handlers.onPlay(); set({ playing: true }); }} onPause={() => set({ playing: false })}
        onWaiting={() => set({ loading: true })} onPlaying={() => set({ loading: false })} onCanPlay={() => set({ loading: false })}
        onLoadedMetadata={(e) => { handlers.onLoadedMetadata(e); set({ duration: e.currentTarget.duration }); }}
        onTimeUpdate={(e) => { handlers.onTimeUpdate(e); set({ current: e.currentTarget.currentTime }); }}
        onSeeked={handlers.onSeeked} onError={handlers.onError} />
      {(!s.playing || s.loading) && (
        <button data-testid="video-big-play" onClick={toggle} aria-label="Play" className="absolute inset-0 m-auto grid h-16 w-16 place-items-center rounded-full bg-nw-red/90 text-white shadow-[0_0_30px_rgba(255,30,39,0.6)]">
          {s.loading && s.playing ? <Loader2 className="h-7 w-7 animate-spin" /> : <Play className="h-7 w-7 translate-x-[2px]" fill="currentColor" />}
        </button>
      )}
      <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-3 pb-2 pt-8 transition-opacity duration-300 ${s.playing ? "opacity-0 group-hover:opacity-100 focus-within:opacity-100" : "opacity-100"}`}>
        <NwSlider value={s.current} max={s.duration} onChange={(v) => { el.current.currentTime = v; }} testId="video-seek-slider" label="Seek" />
        <div className="flex items-center gap-3 text-slate-200">
          <button data-testid="video-play-toggle" onClick={toggle} aria-label={s.playing ? "Pause" : "Play"}>{s.playing ? <Pause className="h-5 w-5" fill="currentColor" /> : <Play className="h-5 w-5" fill="currentColor" />}</button>
          <button data-testid="video-mute-toggle" onClick={toggleMute} aria-label="Mute">{s.muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}</button>
          <div className="hidden sm:block w-24"><NwSlider value={s.muted ? 0 : s.volume} max={1} onChange={setVolume} testId="video-volume-slider" label="Volume" /></div>
          <span data-testid="video-time" className="font-mono text-[11px] text-slate-300">{formatTime(s.current)} / {formatTime(s.duration)}</span>
          <button data-testid="video-fullscreen-toggle" onClick={fullscreen} aria-label="Fullscreen" className="ml-auto">{s.full ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}</button>
        </div>
      </div>
    </div>
  );
};
